import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { UseFormReturn } from 'react-hook-form';
import {
  FormFieldValue,
  FormTreeData,
  NavigationSection,
  SubmissionFormData,
} from '@/features/submission_forms/types';
import { useSubmissionForm } from './useSubmissionForm';
import { useSubmissionProgress } from '@/hooks/submissions/useSubmissionProgress';
import { useSubmissionSave } from './useSubmissionSave';
import { useAutosave } from './useAutosave';
import { useSubmissionState } from './useSubmissionState';
import { usePrintMode } from './usePrintMode';
import { useVisibilityTracking } from './useVisibilityTracking';
import { useVisibleNavigationSections } from './useVisibleNavigationSections';

interface UseDynamicFormOrchestrationOptions {
  form: UseFormReturn;
  formTree: FormTreeData[];
  navigationSections: NavigationSection[];
  savedAnswers?: Record<string, FormFieldValue>;
  onSuccess?: () => void;
}

interface UseDynamicFormOrchestrationReturn {
  // View state
  printView: boolean;
  isSubmitted: boolean;
  isSubmitting: boolean;

  // Save state
  isSaving: boolean;
  lastSaved: string | null | undefined;
  isProgressStale: boolean;
  saveError: string | null;

  // Error state
  submitError: string | null;

  // Progress data
  percentComplete: number;
  visibleNavigationSections: NavigationSection[];

  // Submission data
  submissionFormData: SubmissionFormData | null;
  /** Refetch submission form data (e.g. after micro state update) */
  refreshSubmissionForm: () => Promise<void>;

  // Handlers
  handleSubmit: () => Promise<void>;
  handleVisibilityChange: (sectionId: string, isVisible: boolean) => void;
}

/**
 * Master orchestration hook for DynamicForm
 * Coordinates all submission-related state and logic in one place
 *
 * This hook manages:
 * - Print mode detection
 * - Submission data fetching
 * - Progress tracking
 * - Autosave with staleness
 * - Submission state and actions
 * - Section visibility tracking
 * - Navigation section computation
 */
export const useDynamicFormOrchestration = ({
  form,
  formTree,
  navigationSections,
  savedAnswers,
  onSuccess,
}: UseDynamicFormOrchestrationOptions): UseDynamicFormOrchestrationReturn => {
  const { submissionId } = useParams<{ submissionId: string }>();

  // Print mode
  const { printView } = usePrintMode();

  // Fetch submission data
  const { submissionFormData, fetchSubmissionForm } =
    useSubmissionForm(submissionId);

  // Track visible sections
  const { visibleSections, handleVisibilityChange } = useVisibilityTracking();

  // Fetch and manage progress
  const { progress, refetch: refetchProgress } = useSubmissionProgress(
    submissionId,
    !printView && submissionFormData?.status === 'draft'
  );

  // Handle saving submissions with progress updates
  const { handleSave } = useSubmissionSave({
    submissionId,
    form,
    formTree,
    savedAnswers,
    onProgressRefetch: refetchProgress,
  });

  // Setup autosave with staleness tracking
  const {
    isSaving,
    lastSaved,
    isStale: isProgressStale,
    saveError,
  } = useAutosave({
    form,
    onSave: handleSave,
    savedAnswers,
    enabled:
      submissionFormData?.status === 'draft' && !printView && !!submissionId,
    initialLastSaved: submissionFormData?.last_saved,
  });

  // Manage submission state and logic
  const { isSubmitted, isSubmitting, submitError, handleSubmit } =
    useSubmissionState({
      submissionId,
      onSave: handleSave,
      initialStatus: submissionFormData?.status,
      onSuccess,
    });

  // Sync submission form data on mount and when submissionId changes
  useEffect(() => {
    if (submissionId) {
      fetchSubmissionForm();
    }
  }, [submissionId]); // Only depend on submissionId, not fetchSubmissionForm

  // Calculate progress data
  const percentComplete = progress?.percentage_complete ?? 0;
  const sectionStatusMap = new Map(
    (progress?.section_statuses || []).map(section => [
      section.id,
      section.status,
    ])
  );

  // Compute visible navigation sections with status
  const visibleNavigationSections = useVisibleNavigationSections({
    navigationSections,
    visibleSections,
    sectionStatusMap,
  });

  return {
    // View state
    printView,
    isSubmitted,
    isSubmitting,

    // Save state
    isSaving,
    lastSaved,
    isProgressStale,
    saveError,

    // Error state
    submitError,

    // Progress data
    percentComplete,
    visibleNavigationSections,

    // Submission data
    submissionFormData,
    refreshSubmissionForm: fetchSubmissionForm,

    // Handlers
    handleSubmit,
    handleVisibilityChange,
  };
};
